// openEHR LANG model — BMM package org.openehr.lang.bmm.expression
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { BMM_ROUTINE_TYPE, BMM_TYPE, C_OBJECT, T } from "../_shared.ts";
import {
  BMM_FUNCTION_TYPE,
  BMM_PROCEDURE_TYPE,
  BMM_SIMPLE_TYPE,
  BMM_TUPLE_TYPE,
} from "../_shared.ts";
import type { BMM_FUNCTION, BMM_OPERATOR, BMM_PROCEDURE, BMM_PROPERTY, BMM_READONLY_VARIABLE, BMM_ROUTINE, BMM_STATIC, BMM_WRITABLE_VARIABLE } from "./core/feature.ts";
import type { BMM_LITERAL_VALUE } from "./core/literal_value.ts";
import * as openehr_base from "../../base/mod.ts";

/**
 * Abstract parent of all typed expression meta-types.
 */
export abstract class EL_EXPRESSION {
  /**
   * Meta-type of expression entity used in type-checking and evaluation.
   *
   * Effected in descendants.
   * @returns Result value
   */
  eval_type(): BMM_TYPE {
    return BMM_SIMPLE_TYPE.named("Any");
  }

  /**
   * True if \`_eval_type_\` is notionally Boolean (i.e. a \`BMM_SIMPLE_TYPE\` with \`_type_name()_\` = \`Boolean\`).
   * @returns Result value
   */
  is_boolean(): openehr_base.Boolean {
    try {
      const evalType = this.eval_type();
      if (evalType && evalType.type_name) {
        const typeName = evalType.type_name()?.value || "";
        return openehr_base.Boolean.from(
          typeName.toLowerCase() === "boolean",
        );
      }
    } catch {
      // eval_type may not be implemented in abstract context
    }
    return openehr_base.Boolean.from(false);
  }
}

/**
 * Abstract parent of operator types.
 */
export abstract class EL_OPERATOR extends EL_EXPRESSION {
  /**
   * Internal storage for precedence_overridden
   * @protected
   */
  protected _precedence_overridden?: openehr_base.Boolean;

  /**
   * True if the natural precedence of operators is overridden in the expression represented by this node of the expression tree. If True, parentheses should be introduced around the totality of the syntax expression corresponding to this operator node and its operands.
   */
  get precedence_overridden(): boolean | undefined {
    return this._precedence_overridden?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for precedence_overridden.
   * Use this to access openehr_base.Boolean methods.
   */
  get $precedence_overridden(): openehr_base.Boolean | undefined {
    return this._precedence_overridden;
  }

  /**
   * Sets precedence_overridden from either a primitive value or openehr_base.Boolean wrapper.
   */
  set precedence_overridden(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._precedence_overridden = undefined;
    } else if (typeof val === "boolean") {
      this._precedence_overridden = openehr_base.Boolean.from(val);
    } else {
      this._precedence_overridden = val;
    }
  }

  /**
   * Internal storage for symbol
   * @protected
   */
  protected _symbol?: openehr_base.String;

  /**
   * The symbol actually used in the expression, or intended to be used for serialisation. Must be a member of \`OPERATOR_DEF._symbols_\`.
   */
  get symbol(): string | undefined {
    return this._symbol?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for symbol.
   * Use this to access openehr_base.String methods.
   */
  get $symbol(): openehr_base.String | undefined {
    return this._symbol;
  }

  /**
   * Sets symbol from either a primitive value or openehr_base.String wrapper.
   */
  set symbol(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._symbol = undefined;
    } else if (typeof val === "string") {
      this._symbol = openehr_base.String.from(val);
    } else {
      this._symbol = val;
    }
  }

  /**
   * Function call equivalent to this operator expression, inferred by matching operator against functions defined in interface of principal operand.
   */
  call?: EL_FUNCTION_CALL;
  /**
   * Routine definition for operator overload resolution.
   */
  definition?: BMM_FUNCTION;
  /**
   * Operator definition derived from \`_definition.operator_definition()_\`.
   * @returns Result value
   */
  operator_definition(): BMM_OPERATOR {
    // Return the operator definition from the function definition
    if (this.definition && "operator_definition" in this.definition) {
      return (this.definition as BMM_FUNCTION).operator_definition!;
    }
    throw new Error("No operator definition available");
  }

  /**
   * Function call equivalent to this operator.
   * @returns Result value
   */
  equivalent_call(): EL_FUNCTION_CALL {
    // Return the cached function call, or create one
    if (this.call) {
      return this.call;
    }
    // Create a new function call representing this operator
    const funcCall = new EL_FUNCTION_CALL();
    funcCall.definition = this.definition;
    return funcCall;
  }
}

/**
 * Binary operator expression node.
 */
export class EL_BINARY_OPERATOR extends EL_OPERATOR {
  /**
   * Left operand node.
   */
  left_operand?: EL_EXPRESSION;
  /**
   * Right operand node.
   */
  right_operand?: EL_EXPRESSION;
}

/**
 * Expression entities that are terminals (i.e. leaves) within operator expressions or tuples.
 */
export abstract class EL_TERMINAL extends EL_EXPRESSION {
}

/**
 * Simple terminal i.e. logically atomic expression element.
 */
export abstract class EL_SIMPLE extends EL_TERMINAL {
}

/**
 * Literal value of any type known in the model, including primitive types. Defined via a \`BMM_LITERAL_VALUE\`.
 */
export class EL_LITERAL extends EL_SIMPLE {
  /**
   * The reference item from which the value of this node can be computed.
   */
  value?: BMM_LITERAL_VALUE<BMM_TYPE>;
  /**
   * Return \`_value.type_\`.
   * @returns Result value
   */
  eval_type(): BMM_TYPE {
    if (this.value && this.value.type) {
      return this.value.type;
    }
    // Return a default Any type if no value set
    return BMM_SIMPLE_TYPE.named("Any");
  }
}

/**
 * Unary operator expression node.
 */
export class EL_UNARY_OPERATOR extends EL_OPERATOR {
  /**
   * Operand node.
   */
  operand?: EL_EXPRESSION;
}

/**
 * Meta-type representing a value-generating simple expression.
 */
export abstract class EL_VALUE_GENERATOR extends EL_SIMPLE {
  /**
   * Internal storage for is_writable
   * @protected
   */
  protected _is_writable?: openehr_base.Boolean;

  get is_writable(): boolean | undefined {
    return this._is_writable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_writable.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_writable(): openehr_base.Boolean | undefined {
    return this._is_writable;
  }

  /**
   * Sets is_writable from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_writable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_writable = undefined;
    } else if (typeof val === "boolean") {
      this._is_writable = openehr_base.Boolean.from(val);
    } else {
      this._is_writable = val;
    }
  }

  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Name used to represent the reference or other entity.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * Generated full reference name, based on constituent parts of the entity. Default version outputs \`_name_\` field.
   * @returns Result value
   */
  reference(): openehr_base.String {
    return openehr_base.String.from(this.name || "");
  }
}

/**
 * A reference that is scoped by a containing entity and requires a context qualifier if it is not the currently scoping entity.
 */
export abstract class EL_FEATURE_REF extends EL_VALUE_GENERATOR {
  /**
   * Scoping expression, which must be a \`EL_VALUE_GENERATOR\`.
   */
  scoper?: EL_VALUE_GENERATOR;
  /**
   * Generated full reference name, consisting of scoping elements and \`_name_\` concatenated using dot notation.
   * @returns Result value
   */
  reference(): openehr_base.String {
    const scoperRef = this.scoper?.reference?.()?.value || "";
    const localName = this.name || "";
    if (scoperRef) {
      return openehr_base.String.from(`${scoperRef}.${localName}`);
    }
    return openehr_base.String.from(localName);
  }
}

/**
 * Reference to a writable property.
 */
export class EL_PROPERTY_REF extends EL_FEATURE_REF {
  /**
   * Property definition (within class).
   */
  definition?: BMM_PROPERTY;
  /**
   * Defined to return True.
   */
  override get is_writable(): boolean | undefined {
    return this._is_writable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_writable.
   * Use this to access openehr_base.Boolean methods.
   */
  override get $is_writable(): openehr_base.Boolean | undefined {
    return this._is_writable;
  }

  /**
   * Sets is_writable from either a primitive value or openehr_base.Boolean wrapper.
   */
  override set is_writable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_writable = undefined;
    } else if (typeof val === "boolean") {
      this._is_writable = openehr_base.Boolean.from(val);
    } else {
      this._is_writable = val;
    }
  }

  /**
   * Type definition (i.e. BMM meta-type definition object) of the constant, property or variable, inferred by inspection of the current scoping instance. Return \`_definition.type_\`.
   * @returns Result value
   */
  eval_type(): BMM_TYPE {
    if (this.definition && this.definition.type) {
      return this.definition.type;
    }
    // Return Any type if no definition
    return BMM_SIMPLE_TYPE.named("Any");
  }
}

/**
 * Parent type of predicate of any object reference.
 */
export abstract class EL_PREDICATE extends EL_SIMPLE {
  /**
   * The target instance of this predicate.
   */
  operand?: EL_VALUE_GENERATOR;
  /**
   * Return \`{BMM_MODEL}._boolean_type_definition_()\`.
   * @returns Result value
   */
  eval_type(): BMM_SIMPLE_TYPE {
    // Predicates always return Boolean type
    return BMM_SIMPLE_TYPE.named("Boolean");
  }
}

/**
 * A predicate taking one external variable reference argument, that returns true if the reference is resolvable, i.e. the external value is obtainable.
 *
 * NOTE: probably to be removed.
 */
export class EL_DEFINED extends EL_PREDICATE {
}

/**
 * A call made to a 'closed' agent, i.e. one with no remaining open arguments.
 */
export abstract class EL_AGENT_CALL {
  /**
   * The agent being called.
   */
  agent?: EL_AGENT;
}

/**
 * A call made on a closed function agent, returning a result. Equivalent to an 'application' of a function in Lambda calculus.
 */
export class EL_FUNCTION_CALL extends EL_FEATURE_REF {
  /**
   * The function agent being called.
   */
  agent?: EL_FUNCTION_AGENT = undefined;
  /**
   * Routine definition when this call is built from an operator expression.
   */
  definition?: BMM_FUNCTION;
  /**
   * Defined to return False.
   */
  override get is_writable(): boolean | undefined {
    return this._is_writable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_writable.
   * Use this to access openehr_base.Boolean methods.
   */
  override get $is_writable(): openehr_base.Boolean | undefined {
    return this._is_writable;
  }

  /**
   * Sets is_writable from either a primitive value or openehr_base.Boolean wrapper.
   */
  override set is_writable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_writable = undefined;
    } else if (typeof val === "boolean") {
      this._is_writable = openehr_base.Boolean.from(val);
    } else {
      this._is_writable = val;
    }
  }

  /**
   * Return \`_agent.definition.type_\`.
   * @returns Result value
   */
  eval_type(): BMM_TYPE {
    if (this.agent?.definition?.type) {
      return this.agent.definition.type;
    }
    // Return Any type if no agent definition
    return BMM_SIMPLE_TYPE.named("Any");
  }

  /**
   * Generated full reference name, consisting of any scoping elements, function name and routine parameters enclosed in parentheses.
   * @returns Result value
   */
  reference(): openehr_base.String {
    const scoperRef = this.scoper?.reference?.()?.value || "";
    const funcName = this.name || "";
    const params = "()"; // Parameters would be formatted here
    if (scoperRef) {
      return openehr_base.String.from(`${scoperRef}.${funcName}${params}`);
    }
    return openehr_base.String.from(`${funcName}${params}`);
  }
}

/**
 * A delayed routine call, whose arguments may be open, partially closed or closed. Generated by special reference to a routine, usually via a dedicated keyword, such as 'lambda' or 'agent', or other special syntax.
 *
 * Instances may include closed delayed calls like \`calculate_age (dob="1987-09-13", today="2019-06-03")\` but also partially open calls such as \`format_structure (struct=?, style=3)\`, where \`struct\` is an open argument.
 *
 * Evaluation type (i.e. type of runtime evaluated form) is \`BMM_SIGNATURE\`.
 */
export abstract class EL_AGENT extends EL_FEATURE_REF {
  /**
   * Name of the routine being called.
   */
  override get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  override get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  override set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * Closed arguments of a routine call as a tuple of objects.
   */
  closed_args?: EL_TUPLE;
  /**
   * Optional list of names of open arguments of the call. If not provided, and the \`_name_\` refers to a routine with more arguments than supplied in \`_closed_args_\`, the missing arguments are inferred from the \`_definition_\`.
   */
  open_args?: undefined;
  /**
   * Reference to definition of a routine for which this is an agent, if one exists.
   */
  definition?: BMM_ROUTINE;
  override get is_writable(): boolean | undefined {
    return this._is_writable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_writable.
   * Use this to access openehr_base.Boolean methods.
   */
  override get $is_writable(): openehr_base.Boolean | undefined {
    return this._is_writable;
  }

  /**
   * Sets is_writable from either a primitive value or openehr_base.Boolean wrapper.
   */
  override set is_writable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_writable = undefined;
    } else if (typeof val === "boolean") {
      this._is_writable = openehr_base.Boolean.from(val);
    } else {
      this._is_writable = val;
    }
  }

  /**
   * Eval type is the signature corresponding to the (remaining) open arguments and return type, if any.
   * @returns Result value
   */
  eval_type(): BMM_ROUTINE_TYPE {
    // Return a routine type based on the definition
    const routineType = new BMM_PROCEDURE_TYPE();
    if (this.definition?.parameters) {
      routineType.argument_types = this.definition.parameters
        .filter((p) => p.type)
        .map((p) => p.type!) as BMM_TYPE[];
    }
    return routineType;
  }

  /**
   * True if there are no open arguments.
   * @returns Result value
   */
  is_callable(): openehr_base.Boolean {
    // An agent is callable if all required arguments are closed (bound)
    // For now, assume callable if closed_args match definition parameters
    if (
      !this.definition?.parameters || this.definition.parameters.length === 0
    ) {
      return openehr_base.Boolean.from(true);
    }
    const closedCount = this.closed_args?.items?.length || 0;
    const requiredCount = this.definition.parameters.length;
    return openehr_base.Boolean.from(closedCount >= requiredCount);
  }

  /**
   * Generated full reference name, including scoping elements.
   * @returns Result value
   */
  reference(): openehr_base.String {
    const scoperRef = this.scoper?.reference?.()?.value || "";
    const agentName = this.name || "";
    if (scoperRef) {
      return openehr_base.String.from(`${scoperRef}.${agentName}`);
    }
    return openehr_base.String.from(agentName);
  }
}

/**
 * A single tuple item, with an optional name.
 */
export class EL_TUPLE_ITEM {
  /**
   * Reference to value entity. If Void, this indicates that the item in this position is Void, e.g. within a routine call parameter list.
   */
  item?: EL_EXPRESSION;
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Optional name of tuple item.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }
}

/**
 * An agent whose signature is of a procedure, i.e. has no result type.
 */
export class EL_PROCEDURE_AGENT extends EL_AGENT {
  /**
   * Reference to definition of routine for which this is a call instance.
   */
  override definition?: BMM_PROCEDURE = undefined;
  /**
   * Eval type is the signature corresponding to the (remaining) open arguments and return type, if any.
   * @returns Result value
   */
  eval_type(): BMM_PROCEDURE_TYPE {
    // Return a procedure type based on the definition
    const procType = new BMM_PROCEDURE_TYPE();
    if (this.definition?.parameters) {
      procType.argument_types = this.definition.parameters
        .filter((p) => p.type)
        .map((p) => p.type!) as BMM_TYPE[];
    }
    return procType;
  }
}

/**
 * Defines an array of optionally named items each of any type.
 */
export class EL_TUPLE extends EL_EXPRESSION {
  /**
   * Items in the tuple, potentially with names. Typical use is to represent an argument list to routine call.
   */
  items?: EL_TUPLE_ITEM[];
  /**
   * Static type inferred from literal value.
   */
  type?: BMM_TUPLE_TYPE;
  /**
   * Return \`_type_\`.
   * @returns Result value
   */
  eval_type(): BMM_TYPE {
    // Return the tuple type if set, otherwise create a new one
    if (this.type) {
      return this.type;
    }
    return new BMM_TUPLE_TYPE();
  }
}

/**
 * An agent whose signature is of a function, i.e. has a result type.
 */
export class EL_FUNCTION_AGENT extends EL_AGENT {
  /**
   * Reference to definition of a routine for which this is a direct call instance, if one exists.
   */
  override definition?: BMM_FUNCTION = undefined;
  /**
   * Eval type is the signature corresponding to the (remaining) open arguments and return type, if any.
   * @returns Result value
   */
  eval_type(): BMM_FUNCTION_TYPE {
    // Return a function type based on the definition
    const funcType = new BMM_FUNCTION_TYPE();
    if (this.definition?.parameters) {
      funcType.argument_types = this.definition.parameters
        .filter((p) => p.type)
        .map((p) => p.type!) as BMM_TYPE[];
    }
    if (this.definition?.result?.type) {
      funcType.result_type = this.definition.result.type;
    }
    return funcType;
  }
}

/**
 * A predicate on any object reference (including function call) that returns True if the reference is attached, i.e. non-Void.
 */
export class EL_ATTACHED extends EL_PREDICATE {
}

/**
 * Abstract parent for second-order constrained forms of first-order expression meta-types.
 */
export abstract class EL_CONSTRAINED extends EL_EXPRESSION {
  /**
   * The base expression of this constrained form.
   */
  base_expression?: EL_EXPRESSION;
}

/**
 * Boolean-returning expression.
 */
export class EL_BOOLEAN_EXPRESSION extends EL_CONSTRAINED {
}

/**
 * Meta-type for reference to a non-abstract type as an object. Assumed to be accessible at run-time. Typically represented syntactically as \`TypeName\` or \`{TypeName}\`.
 *
 * May be used as a value, or as the qualifier for a function or constant access.
 */
export class EL_TYPE_REF extends EL_VALUE_GENERATOR {
  /**
   * Type, directly from the name of the reference, e.g. \`{SOME_TYPE}\`.
   */
  type?: BMM_TYPE;
  /**
   * Internal storage for is_mutable
   * @protected
   */
  protected _is_mutable?: openehr_base.Boolean;

  get is_mutable(): boolean | undefined {
    return this._is_mutable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_mutable.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_mutable(): openehr_base.Boolean | undefined {
    return this._is_mutable;
  }

  /**
   * Sets is_mutable from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_mutable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_mutable = undefined;
    } else if (typeof val === "boolean") {
      this._is_mutable = openehr_base.Boolean.from(val);
    } else {
      this._is_mutable = val;
    }
  }

  /**
   * Return \`_type_\`.
   * @returns Result value
   */
  eval_type(): BMM_TYPE {
    // Return the type if set, otherwise return Any type
    if (this.type) {
      return this.type;
    }
    return BMM_SIMPLE_TYPE.named("Any");
  }
}

/**
 * Abstract parent of meta-types representing a branch of some kind of decision structure. Defines \`result\` as being of the generic type \`T\`.
 */
export abstract class EL_DECISION_BRANCH<T extends EL_TERMINAL> {
  /**
   * Result expression of conditional, if its \`_condition_\` evaluates to True.
   */
  result?: T;
}

/**
 * Conditional structure used in condition chain expressions. Evaluated by evaluating its \`_condition_\`, which is a Boolean-returning expression, and if this returns True, the result is the evaluation result of \`_expression_\`.
 */
export class EL_CONDITIONAL_EXPRESSION<T extends EL_TERMINAL>
  extends EL_DECISION_BRANCH<T> {
  /**
   * Boolean expression defining the condition of this decision branch.
   */
  condition?: EL_EXPRESSION;
}

/**
 * Meta-type for decision tables. Generic on the meta-type of the \`_result_\` attribute of the branches, to allow specialised forms of if/else and case structures to be created.
 */
export abstract class EL_DECISION_TABLE<T extends EL_TERMINAL>
  extends EL_TERMINAL {
  /**
   * Members of the chain, equivalent to branches in an if/then/else chain and cases in a case statement.
   */
  items?: undefined;
  /**
   * Result expression of conditional, if its \`_condition_\` evaluates to True.
   */
  else?: T;
}

/**
 * Compound expression consisting of a chain of condition-gated expressions, and an ungated \`_else_\` member that as a whole, represents an if/then/elseif/else chains.
 *
 * Evaluated by iterating through \`_items_\` and for each one, evaluating its \`_condition_\`, which if True, causes the evaluation result of the chain to be that item's \`_result_\` evaluation result.
 *
 * If no member of \`_items_\` has a True-returning \`_condition_\`, the evaluation result is the result of evaluating the \`_else_\` expression.
 */
export class EL_CONDITION_CHAIN<T extends EL_TERMINAL>
  extends EL_DECISION_TABLE<T> {
}

/**
 * Compound expression consisting of a list of value-range / expression pairs, and an  \`_else_\` member that as a whole, represents a case statement flavour of decision table.
 *
 * Evaluated by iterating through \`_items_\` and for each one, comparing \`_input_\` to the item \`_value_range_\`. If the \`_input_\` is in the range, the evaluation result of the table is that item's \`_result_\` evaluation result.
 *
 * If no member of \`_items_\` has a True-returning \`_condition_\`, the evaluation result is the result of evaluating the \`_else_\` expression.
 */
export class EL_CASE_TABLE<T extends EL_TERMINAL> extends EL_DECISION_TABLE<T> {
  /**
   * Expressing generating the input value for the case table.
   */
  test_value?: EL_VALUE_GENERATOR;
}

/**
 * One branch of a Case table, consisting of a value constraint (the match criterion) and a result, of the generic parameter type T.
 */
export class EL_CASE<T extends EL_TERMINAL> extends EL_DECISION_BRANCH<T> {
  /**
   * Constraint on
   */
  value_constraint?: C_OBJECT;
}

/**
 * Abstract meta-type of any kind of symbolic variable.
 */
export abstract class EL_VARIABLE extends EL_VALUE_GENERATOR {
}

/**
 * Reference to a writable property, either a constant or computed.
 */
export class EL_STATIC_REF extends EL_FEATURE_REF {
  /**
   * Constant definition (within class).
   */
  definition?: BMM_STATIC;
  /**
   * Defined to return False.
   */
  override get is_writable(): boolean | undefined {
    return this._is_writable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_writable.
   * Use this to access openehr_base.Boolean methods.
   */
  override get $is_writable(): openehr_base.Boolean | undefined {
    return this._is_writable;
  }

  /**
   * Sets is_writable from either a primitive value or openehr_base.Boolean wrapper.
   */
  override set is_writable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_writable = undefined;
    } else if (typeof val === "boolean") {
      this._is_writable = openehr_base.Boolean.from(val);
    } else {
      this._is_writable = val;
    }
  }
}

/**
 * Meta-type of writable variables, including routine locals and the special variable 'Result'.
 */
export class EL_WRITABLE_VARIABLE extends EL_VARIABLE {
  /**
   * Variable definition to which this reference refers.
   */
  definition?: BMM_WRITABLE_VARIABLE;
  /**
   * Defined to return True in all cases.
   */
  override get is_writable(): boolean | undefined {
    return this._is_writable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_writable.
   * Use this to access openehr_base.Boolean methods.
   */
  override get $is_writable(): openehr_base.Boolean | undefined {
    return this._is_writable;
  }

  /**
   * Sets is_writable from either a primitive value or openehr_base.Boolean wrapper.
   */
  override set is_writable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_writable = undefined;
    } else if (typeof val === "boolean") {
      this._is_writable = openehr_base.Boolean.from(val);
    } else {
      this._is_writable = val;
    }
  }
}

/**
 * Meta-type of read-only variables, including routine parameter and the special variable 'Self'.
 */
export class EL_READONLY_VARIABLE extends EL_VARIABLE {
  /**
   * Variable definition to which this reference refers.
   */
  definition?: BMM_READONLY_VARIABLE;
  /**
   * Defined to return False in all cases.
   */
  override get is_writable(): boolean | undefined {
    return this._is_writable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_writable.
   * Use this to access openehr_base.Boolean methods.
   */
  override get $is_writable(): openehr_base.Boolean | undefined {
    return this._is_writable;
  }

  /**
   * Sets is_writable from either a primitive value or openehr_base.Boolean wrapper.
   */
  override set is_writable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_writable = undefined;
    } else if (typeof val === "boolean") {
      this._is_writable = openehr_base.Boolean.from(val);
    } else {
      this._is_writable = val;
    }
  }
}
